import { useState } from "react";

/* TEMPORARY on-device audio diagnostic (preview only).
   Open this page ON the iPhone: {preview-url}/audio-debug
   Tap each test and read the results — they pinpoint which link in the
   iOS audio chain breaks (context state, render clock, oscillator output,
   or the media-playback path used by the mute switch / audio routing). */

export default function AudioDebug() {
  const [lines, setLines] = useState([]);
  const log = (s) => setLines((l) => [...l, s]);

  const testWebAudio = async () => {
    setLines([]);
    const AC = window.AudioContext || window.webkitAudioContext;
    log(`AudioContext API: ${AC ? "present" : "MISSING"}`);
    if (!AC) return;
    const ctx = new AC();
    log(`state at creation (inside tap): ${ctx.state}`);
    try {
      await ctx.resume();
    } catch (e) {
      log(`resume() threw: ${e.message}`);
    }
    log(`state after resume(): ${ctx.state}`);
    log(`sampleRate: ${ctx.sampleRate} | baseLatency: ${ctx.baseLatency}`);

    const t0 = ctx.currentTime;
    let ended = false;
    const gain = ctx.createGain();
    gain.gain.value = 0.3;
    gain.connect(ctx.destination);
    const o = ctx.createOscillator();
    o.frequency.value = 440;
    o.connect(gain);
    o.onended = () => {
      ended = true;
    };
    o.start();
    o.stop(ctx.currentTime + 0.5);
    log("playing 440Hz beep for 0.5s NOW — listen");

    await new Promise((r) => setTimeout(r, 1200));
    log(`render clock advanced: ${(ctx.currentTime - t0).toFixed(2)}s (>0.4 = audio graph really rendering)`);
    log(`oscillator reached its end: ${ended ? "YES (output consumed)" : "NO (graph suspended/starved)"}`);
    log(`final state: ${ctx.state}`);
    log(ended && ctx.state === "running"
      ? "=> Web Audio path is HEALTHY on this device"
      : "=> Web Audio path is BLOCKED (gesture policy or routing)");
  };

  const testMediaElement = async () => {
    // 0.5s 440Hz WAV via data URI — exercises the HTMLAudioElement path,
    // which uses a different AVAudioSession category on iOS (playback,
    // unaffected by the mute switch).
    const sr = 8000, n = sr / 2, buf = new Int16Array(n);
    for (let i = 0; i < n; i++) buf[i] = Math.sin((2 * Math.PI * 440 * i) / sr) * 32767 * 0.4;
    const header = new Uint8Array(44);
    const dv = new DataView(header.buffer);
    const wstr = (o, s) => s.split("").forEach((c, i) => dv.setUint8(o + i, c.charCodeAt(0)));
    wstr(0, "RIFF"); dv.setUint32(4, 36 + n * 2, true); wstr(8, "WAVEfmt ");
    dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
    dv.setUint32(24, sr, true); dv.setUint32(28, sr * 2, true); dv.setUint16(32, 2, true);
    dv.setUint16(34, 16, true); wstr(36, "data"); dv.setUint32(40, n * 2, true);
    const bytes = new Uint8Array(44 + n * 2);
    bytes.set(header); bytes.set(new Uint8Array(buf.buffer), 44);
    let bin = "";
    bytes.forEach((b) => (bin += String.fromCharCode(b)));
    const a = new Audio("data:audio/wav;base64," + btoa(bin));
    try {
      await a.play();
      log("HTMLAudioElement: play() succeeded — beep should be audible (even with mute switch ON)");
    } catch (e) {
      log(`HTMLAudioElement play() failed: ${e.message}`);
    }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] p-6 font-body text-white" data-testid="audio-debug-page">
      <h1 className="font-heading text-2xl font-black">Audio diagnostic</h1>
      <p className="mt-2 text-sm text-zinc-400">
        Run on your iPhone, volume up. Report what appears below (a screenshot
        is perfect).
      </p>
      <div className="mt-6 space-y-3">
        <button
          type="button"
          data-testid="test-webaudio-btn"
          onClick={testWebAudio}
          className="w-full rounded-full bg-[#E52525] px-6 py-4 text-sm font-semibold"
        >
          1. Test Web Audio (the ringback path)
        </button>
        <button
          type="button"
          data-testid="test-mediaelement-btn"
          onClick={testMediaElement}
          className="w-full rounded-full border border-white/15 px-6 py-4 text-sm font-semibold"
        >
          2. Test audio element (comparison path)
        </button>
      </div>
      <pre className="mt-6 whitespace-pre-wrap rounded-2xl border border-white/10 bg-[#141414] p-4 text-xs leading-relaxed text-zinc-300">
        {lines.length ? lines.join("\n") : "results appear here…"}
      </pre>
    </main>
  );
}
